/**
 * Generates the behavior pack script (@minecraft/server + server-ui) that opens
 * the screens drawn in the editor.
 *
 * The link between script and JSON UI is the form TITLE: the script sends
 * `form.title("My Screen")` and server_form.json shows the screen whose name is
 * in the title. That makes the screen name the real identifier, not a label.
 *
 * Buttons carry an action, so a screen can also drive scoreboards: the script
 * creates the objectives it needs and reads scores back into the button text
 * through the `{score:objective}` placeholder.
 */
import { AddonScreen, sanitizeFlag } from "./AddonTypes";
import { ButtonAction, collectFormButtons } from "./FormButtons";
import { TextureResolver } from "./ScreenSerializer";

export interface ScriptOptions {
  triggerItem: string;
  /** Maps editor textures onto pack paths, for the button icons. */
  resolveTexture?: TextureResolver;
  /**
   * Objective shown on the vanilla sidebar - the scoreboard in the top right
   * corner. JSON UI cannot read an arbitrary objective, so the sidebar content
   * comes from the game and a pack can only restyle it.
   */
  sidebarObjective?: string;
}

export interface ScreenScriptData {
  flag: string;
  /**
   * What the player reads. The form title carries the routing flag first and
   * this after it, so a label wired to the title subtracts the flag and is
   * left with exactly this text.
   */
  title: string;
  buttons: Array<{ text: string; icon?: string; action: ButtonAction }>;
}

/** A screen name into something worth putting on the screen: `hub_form` -> `Hub Form`. */
function displayTitle(name: string): string {
  return name
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/\S+/g, word => word[0].toUpperCase() + word.slice(1));
}

/** Escapes a string so it fits between double quotes in the generated code. */
const quote = (value: string): string => `"${value.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`;

/** Objectives referenced anywhere - the script creates them if they are missing. */
function collectObjectives(data: ScreenScriptData[]): string[] {
  const out = new Set<string>();
  for (const screen of data) {
    for (const button of screen.buttons) {
      if (button.action.type === "scoreboard" && button.action.objective) {
        out.add(button.action.objective);
      }
      for (const match of button.text.matchAll(/\{score:([A-Za-z0-9_.-]+)\}/g)) {
        out.add(match[1]);
      }
    }
  }
  return [...out];
}

export function describeScreens(screens: AddonScreen[], options: ScriptOptions): ScreenScriptData[] {
  return screens.map(screen => {
    const buttons: ScreenScriptData["buttons"] = collectFormButtons(screen.root).map(button => ({
      text: button.text,
      icon: button.icon && options.resolveTexture ? options.resolveTexture(button.icon) : button.icon,
      action: button.action
    }));

    // An ActionForm without buttons never opens. A screen with none gets a
    // spare one: no JSON UI control points at that index, but the form exists.
    if (buttons.length === 0) buttons.push({ text: "Close", action: { type: "none" } });

    return { flag: sanitizeFlag(screen.name), title: displayTitle(screen.name), buttons };
  });
}

function actionLiteral(action: ButtonAction): string {
  const parts: string[] = [`type: ${quote(action.type)}`];
  if (action.value) parts.push(`value: ${quote(action.value)}`);
  if (action.type === "scoreboard") {
    if (action.objective) parts.push(`objective: ${quote(action.objective)}`);
    parts.push(`operation: ${quote(action.operation ?? "add")}`);
    parts.push(`amount: ${Math.round(action.amount ?? 1)}`);
  }
  return `{ ${parts.join(", ")} }`;
}

function screensLiteral(data: ScreenScriptData[]): string {
  const entries = data.map(screen => {
    const buttons = screen.buttons
      .map(button => {
        const fields = [`text: ${quote(button.text)}`];
        if (button.icon) fields.push(`icon: ${quote(button.icon)}`);
        fields.push(`action: ${actionLiteral(button.action)}`);
        return `            { ${fields.join(", ")} },`;
      })
      .join("\n");
    return `    ${quote(screen.flag)}: {\n        title: ${quote(screen.title)},\n        buttons: [\n${buttons}\n        ],\n    },`;
  });
  return `{\n${entries.join("\n")}\n}`;
}

/** Builds the `scripts/main.js` of the behavior pack. */
export function generateScript(screens: AddonScreen[], options: ScriptOptions): string {
  const data = describeScreens(screens, options);
  const first = data[0]?.flag ?? "Custom UI";
  const objectives = collectObjectives(data);
  if (options.sidebarObjective && !objectives.includes(options.sidebarObjective)) {
    objectives.push(options.sidebarObjective);
  }

  return `import { world, system } from "@minecraft/server";
import { ActionFormData } from "@minecraft/server-ui";

/**
 * Generated by JsonForge.
 *
 * The form TITLE is what makes the resource pack pick a screen. If you rename
 * a screen here, rename it in the editor too - otherwise the game opens the
 * vanilla form instead of your screen.
 */

const TRIGGER_ITEM = ${quote(options.triggerItem)};

/** Objective displayed on the vanilla sidebar, empty to leave it alone. */
const SIDEBAR_OBJECTIVE = ${quote(options.sidebarObjective ?? "")};

/** Objectives this pack reads or writes. Created on load when missing. */
const OBJECTIVES = ${JSON.stringify(objectives)};

/** Button order MUST match the editor order (collection_index). */
const SCREENS = ${screensLiteral(data)};

function ensureObjective(id) {
    let objective = world.scoreboard.getObjective(id);
    if (!objective) objective = world.scoreboard.addObjective(id, id);
    return objective;
}

function getScore(player, id) {
    const objective = ensureObjective(id);
    try {
        return objective.getScore(player) ?? 0;
    } catch {
        return 0;
    }
}

function applyScoreboard(player, action) {
    if (!action.objective) return;
    const objective = ensureObjective(action.objective);
    const current = getScore(player, action.objective);
    const amount = action.amount ?? 0;

    if (action.operation === "reset") {
        try {
            objective.removeParticipant(player);
        } catch {
            // The player simply has no score yet.
        }
        return;
    }

    const next =
        action.operation === "set"
            ? amount
            : action.operation === "remove"
              ? current - amount
              : current + amount;
    objective.setScore(player, next);
}

/** Replaces {score:objective} in a label with the player's current score. */
function fillScores(player, text) {
    return text.replace(/\\{score:([A-Za-z0-9_.-]+)\\}/g, (_match, id) => String(getScore(player, id)));
}

function runAction(player, action) {
    if (!action || action.type === "none") return;

    switch (action.type) {
        case "screen":
            if (action.value) system.run(() => showScreen(player, action.value));
            return;
        case "command":
            if (action.value) player.runCommand(action.value);
            return;
        case "scoreboard":
            applyScoreboard(player, action);
            return;
        default:
            player.sendMessage(fillScores(player, action.value ?? "§aClicked"));
    }
}

function showScreen(player, name) {
    const screen = SCREENS[name];
    if (!screen) {
        console.warn("Unknown screen: " + name);
        return;
    }

    // The flag routes, the title is read: server_form.json looks for the name
    // inside the title and the screen's own label subtracts it back out.
    const form = new ActionFormData().title(name + screen.title);
    for (const button of screen.buttons) {
        const label = fillScores(player, button.text);
        if (button.icon) form.button(label, button.icon);
        else form.button(label);
    }

    form.show(player).then((response) => {
        if (response.canceled) return;
        const clicked = screen.buttons[response.selection];
        if (clicked) runAction(player, clicked.action);
    });
}

function showSidebar() {
    if (!SIDEBAR_OBJECTIVE) return;
    ensureObjective(SIDEBAR_OBJECTIVE);
    // The command works on every API version, unlike the display slot enum.
    world
        .getDimension("overworld")
        .runCommand("scoreboard objectives setdisplay sidebar " + SIDEBAR_OBJECTIVE);
}

// The event is called worldLoad on the 2.x API and worldInitialize on 1.x.
const worldReady = world.afterEvents.worldLoad ?? world.afterEvents.worldInitialize;
worldReady?.subscribe(() => {
    for (const id of OBJECTIVES) ensureObjective(id);
    system.run(showSidebar);
});

world.afterEvents.itemUse.subscribe((event) => {
    if (event.itemStack?.typeId !== TRIGGER_ITEM) return;
    const player = event.source;
    system.run(() => showScreen(player, ${quote(first)}));
});
`;
}
